import _ from 'lodash';

import { bindServices } from '@/services/api';

const api = bindServices('lists');
import { getFiltersGlobalFilters, toPaginate } from '@/utils/infrastructure';
import CoreQueries from '@/utils/input/queries';
import { updateFavorite } from './service';
import { filtersConfig } from './utils';
import { groupsFiltersConfig } from './manage-groups/utils';

import './utils';

const meta = {
  page: 1,
  limit: 5,
  count: 0,
  totalPages: 0,
};

const initialPaginationTaskTrace = { page: 1, count: 0, totalPages: 0 };

const initialState = {
  data: [],
  groups: [],
  favorites: [],
  currentGroup: {},
  searchCriteria: '',
  listPagination: meta,
  groupsPagination: meta,
  favoritesPagination: meta,
  orderBy: '',
  filtersConfig,
  groupsFiltersConfig,
  favoritesChanged: false,
  openListTaskTraceDrawer: false,
  listTaskTraceListId: null,
  listTaskTraceList: [],
  paginationListTaskTrace: { ...initialPaginationTaskTrace },
  orderByListTaskTrace: '',
  listTaskTraceSearchTable: '',
  openListTaskTraceDetail: false,
  listTaskTraceDetailId: null,
  selectedListTaskTraceItem: null,
  listTaskTraceDetailLoading: false,
  listTaskTracePageSize: meta.limit,
};

const Model = {
  namespace: 'lists',
  state: initialState,
  effects: {
    *fetchGroups({ payload }, { call, put }) {
      try {
        const params = {
          route: 'list/groups',
          ...CoreQueries.ManageLists.groups,
          ...payload,
        };

        const res = yield call(api.getAll, params);

        yield put({
          type: 'queryGroups',
          payload: {
            ...res,
            ..._.pick(payload, ['page', 'limit']),
          },
        });
      } catch (e) {}
    },

    // Main management view (`/custom-lists` sidebar menu) →
    // `/list/manage`. Per the UX guide `/manage` is reserved for
    // top-level management screens accessed from the left sidebar;
    // every other context (selectors, pickers, embedded dialogs,
    // byList legacy) uses `/list/allowed`. `canManage` gates
    // configuration UI; `isFavorite` drives the star icon on each card.
    *fetchLists({ payload }, { call, put }) {
      try {
        const res = yield call(api.getAll, {
          route: 'list/manage',
          ...CoreQueries.ManageLists.paged,
          select: `${CoreQueries.ManageLists.paged.select},canManage,isFavorite`,
          ...payload,
        });
        yield put({
          type: 'queryList',
          payload: { ...res, ..._.pick(payload, ['page', 'limit']) },
        });
      } catch (e) {}
    },

    // Favorites-first load via the dedicated `/list/favorites`
    // endpoint. `canManage` is required for the row-command kebab and
    // `isFavorite` keeps the star icon filled (the endpoint returns
    // only favorites but the flag isn't auto-included in the select).
    *fetchFavorites({ payload }, { call, put }) {
      try {
        const res = yield call(api.getAll, {
          route: 'list/favorites',
          ...CoreQueries.ManageLists.favorites,
          select: `${CoreQueries.ManageLists.favorites.select},canManage,isFavorite`,
          ...payload,
        });
        yield put({
          type: 'queryFavorites',
          payload: { ...res, ..._.pick(payload, ['page', 'limit']) },
        });
      } catch (e) {}
    },

    // Star toggle — mirrors the `/processes` contract:
    //   1. `querySetFavorite` flips the flag locally (optimistic).
    //   2. `markFavoritesChanged` flags the slice so the next time the
    //      user lands on the favorites tab, `onTabChange` refreshes it.
    // No re-fetch happens here: re-reading `/list/manage` or
    // `/list/favorites` right after the POST/DELETE clobbers the
    // optimistic update with potentially-stale replica data.
    *setFavorite({ payload }, { call, put }) {
      try {
        yield call(updateFavorite, payload);
        yield put({ type: 'querySetFavorite', payload });
        yield put({ type: 'markFavoritesChanged' });
      } catch (e) {}
    },

    *search({ payload }, { put }) {
      yield put({ type: 'querySearch', payload });
    },

    // Only refresh the favorites tab when the slice has been touched
    // since the last fetch (`favoritesChanged`). Same contract as
    // `/processes`: a clean re-entry keeps the cached list intact.
    *onTabChange({ payload }, { put, select }) {
      if (payload === 'favorites') {
        const { favoritesChanged } = yield select((state) => state.lists);
        if (favoritesChanged) {
          yield put({ type: 'fetchFavorites', payload: { page: 1 } });
          yield put({ type: 'clearFavoritesChanged' });
        }
      }
    },

    *paginate({ payload }, { put }) {
      yield put({ type: 'queryPage', payload });
    },

    *setOrderBy({ payload }, { put }) {
      yield put({ type: 'orderBy', payload });
    },

    *createList({ payload }, { call, put }) {
      try {
        const res = yield call(api.create, {
          route: 'customList',
          data: payload,
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'fetchLists', payload: { page: 1, readPriority: true } });
        return res;
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
            validationErrors: e.validationErrors,
          });
        }
        return null;
      }
    },

    *deleteItem({ id, params }, { call, put }) {
      try {
        yield call(api.apiRoute, {
          route: `customList/${id}`,
          method: 'delete',
        });
        yield put({
          type: 'queryDeleteItem',
          payload: { id },
        });
        yield put({
          type: 'fetchLists',
          payload: { ...params, readPriority: true },
        });
      } catch (e) {}
    },

    *toggleActive({ payload }, { call, put }) {
      try {
        yield call(api.apiRoute, {
          route: `customList/activate/${payload.id}/${payload.isActive}`,
          method: 'post',
        });
        yield put({
          type: 'queryToggleActive',
          payload: { id: payload.id, isActive: payload.isActive },
        });
      } catch (e) {}
    },

    *reset(o, { put }) {
      yield put({ type: 'resetState' });
    },

    *toggleListTaskTraceDrawer({ payload }, { put }) {
      yield put({
        type: 'queryToggleListTaskTraceDrawer',
        payload,
      });
    },

    *fetchListTaskTraceList({ payload }, { call, put }) {
      try {
        const filterStr = payload.filter
          ? getFiltersGlobalFilters({ listTaskTraces: payload.filter }, 'listTaskTraces')
          : null;
        const params = {
          route: `list/taskTrace/${payload.listId}`,
          page: payload.page,
          limit: payload.limit,
          search: payload.search,
          orderBy: payload.orderBy,
          ...(filterStr && { filter: filterStr }),
          ...CoreQueries.ManageListTaskTrace.paged,
        };
        const response = yield call(api.getAll, params);
        yield put({
          type: 'queryListTaskTraceList',
          payload: {
            ...response,
            page: payload.page,
            limit: payload.limit,
          },
        });
      } catch (e) {
        /*  */
      }
    },

    *toggleListTaskTraceDetail({ payload }, { put }) {
      yield put({
        type: 'queryToggleListTaskTraceDetail',
        payload,
      });
    },

    *fetchListTaskTraceDetail({ payload }, { call, put }) {
      try {
        yield put({ type: 'queryListTaskTraceDetailStart', payload });
        const res = yield call(api.getById, {
          route: `list/taskTrace/${payload.listId}`,
          id: payload.taskTraceId,
          ...CoreQueries.ManageListTaskTrace.detail,
        });
        yield put({ type: 'queryListTaskTraceDetailSuccess', payload: res });
      } catch (e) {
        yield put({ type: 'queryListTaskTraceDetailFail' });
      }
    },

    *setOrderListTaskTrace({ payload }, { put }) {
      yield put({
        type: 'querySetOrderListTaskTrace',
        payload,
      });
    },

    *setListTaskTraceSearchTable({ payload }, { put }) {
      yield put({
        type: 'querySetListTaskTraceSearchTable',
        payload,
      });
    },

    *clearListTaskTraceList({ payload }, { put }) {
      yield put({
        type: 'queryClearListTaskTraceList',
        payload,
      });
    },
  },
  reducers: {
    queryGroups(state, { payload }) {
      return {
        ...state,
        groups: payload?.items || [],
        groupsPagination: {
          ...state.groupsPagination,
          ...payload,
          ...toPaginate(payload),
        },
      };
    },

    queryList(state, { payload }) {
      return {
        ...state,
        data: payload?.items || [],
        listPagination: {
          ...state.listPagination,
          ...payload,
          ...toPaginate(payload),
        },
      };
    },

    queryFavorites(state, { payload }) {
      return {
        ...state,
        favorites: payload?.items || [],
        favoritesPagination: {
          ...state.favoritesPagination,
          ...payload,
          ...toPaginate(payload),
        },
      };
    },

    // Optimistic favorite toggle: flips `isFavorite` on the main grid
    // (`data`) AND inserts/removes the item from the `favorites` slice
    // so the favorites tab reflects the change with zero network
    // traffic. The slice is reconciled the next time the user opens
    // the favorites tab (`onTabChange` → `fetchFavorites`).
    querySetFavorite(state, { payload }) {
      const { id, isFavorite } = payload;
      const next = !isFavorite;
      const updateItem = (item) => (item.id === id ? { ...item, isFavorite: next } : item);

      let favorites = state.favorites;
      if (next) {
        const target = state.data.find((it) => it.id === id);
        if (target && !favorites.some((f) => f.id === id)) {
          favorites = [...favorites, { ...target, isFavorite: true }];
        }
      } else {
        favorites = favorites.filter((f) => f.id !== id);
      }

      return {
        ...state,
        data: state.data.map(updateItem),
        favorites,
      };
    },

    markFavoritesChanged(state) {
      return { ...state, favoritesChanged: true };
    },

    clearFavoritesChanged(state) {
      return { ...state, favoritesChanged: false };
    },

    queryToggleActive(state, { payload }) {
      return {
        ...state,
        data: state.data.map((item) =>
          item.id === payload.id ? { ...item, isActive: payload.isActive } : item,
        ),
      };
    },

    querySearch(state, { payload }) {
      return {
        ...state,
        ...(_.isString(payload) && { searchCriteria: payload }),
        ...(_.isObject(payload) && {
          [`search${_.capitalize(payload.viewContext)}Criteria`]: payload.criteria,
        }),
      };
    },

    orderBy(state, { payload }) {
      return {
        ...state,
        orderBy: payload,
      };
    },

    queryDeleteItem(state, { payload }) {
      return {
        ...state,
        data: state.data.filter((item) => item.id !== payload.id),
      };
    },

    resetState() {
      return initialState;
    },

    queryToggleListTaskTraceDrawer(state, { payload }) {
      const base = {
        ...state,
        openListTaskTraceDrawer: payload.state,
        listTaskTraceListId: payload.state ? payload.id : null,
      };
      if (!payload.state) {
        return {
          ...base,
          listTaskTraceList: [],
          paginationListTaskTrace: { ...initialPaginationTaskTrace },
          orderByListTaskTrace: '',
          listTaskTraceSearchTable: '',
          listTaskTracePageSize: meta.limit,
          openListTaskTraceDetail: false,
          listTaskTraceDetailId: null,
          selectedListTaskTraceItem: null,
          listTaskTraceDetailLoading: false,
        };
      }
      return { ...base, orderByListTaskTrace: 'created desc', listTaskTraceSearchTable: '' };
    },

    queryListTaskTraceList(state, { payload }) {
      return {
        ...state,
        listTaskTraceList: payload.items || [],
        paginationListTaskTrace: _.merge(state.paginationListTaskTrace, toPaginate(payload)),
      };
    },

    querySetListTaskTraceSearchTable(state, { payload }) {
      return {
        ...state,
        listTaskTraceSearchTable: payload ?? '',
      };
    },

    queryClearListTaskTraceList(state) {
      return {
        ...state,
        listTaskTraceList: [],
        paginationListTaskTrace: { ...initialPaginationTaskTrace },
      };
    },

    querySetOrderListTaskTrace(state, { payload }) {
      return {
        ...state,
        orderByListTaskTrace: payload.value,
      };
    },

    querySetListTaskTracePageSize(state, { payload }) {
      return {
        ...state,
        listTaskTracePageSize: payload,
      };
    },

    queryToggleListTaskTraceDetail(state, { payload }) {
      return {
        ...state,
        openListTaskTraceDetail: payload.state,
        listTaskTraceDetailId: payload.state ? payload.taskTraceId : null,
        selectedListTaskTraceItem: payload.state && payload.item ? payload.item : null,
        ...(!payload.state && { listTaskTraceDetailLoading: false }),
      };
    },

    queryListTaskTraceDetailStart(state, { payload }) {
      return {
        ...state,
        openListTaskTraceDetail: true,
        listTaskTraceDetailId: payload.taskTraceId,
        selectedListTaskTraceItem: null,
        listTaskTraceDetailLoading: true,
      };
    },

    queryListTaskTraceDetailSuccess(state, { payload }) {
      return {
        ...state,
        selectedListTaskTraceItem: payload,
        listTaskTraceDetailLoading: false,
      };
    },

    queryListTaskTraceDetailFail(state) {
      return {
        ...state,
        openListTaskTraceDetail: false,
        listTaskTraceDetailId: null,
        listTaskTraceDetailLoading: false,
        selectedListTaskTraceItem: null,
      };
    },
  },
};

export default Model;
